import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Home, Users, ChevronDown, ChevronUp, Search, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

import { getAuthToken } from '../../services/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getToken = () => getAuthToken();

export function VillaOccupancyTile({ onClick, data }) {
  const occupancyRate = data?.total_villas
    ? Math.round((data.full / data.total_villas) * 100)
    : 0;

  return (
    <Card
      className="shadow-lg border-0 cursor-pointer hover:shadow-xl transition-shadow"
      data-testid="villa-occupancy-card"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-600 mb-1">Villa Occupancy</p>
            <p className="text-3xl font-bold text-indigo-600">
              {data?.total_villas || 0}
            </p>
          </div>
          <Home className="w-8 h-8 text-indigo-600" />
        </div>
        <div className="mt-2">
          <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
            <span>{data?.full || 0} full</span>
            <span>{occupancyRate}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-1.5">
            <div
              className="h-1.5 rounded-full transition-all duration-500 bg-indigo-600"
              style={{ width: `${occupancyRate}%` }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const STATUS_CONFIG = {
  full: { label: 'Full', bg: 'bg-red-100', text: 'text-red-700', badge: 'bg-red-600' },
  partial: { label: 'Available', bg: 'bg-amber-50', text: 'text-amber-700', badge: 'bg-amber-500' },
  available: { label: 'Empty', bg: 'bg-emerald-50', text: 'text-emerald-700', badge: 'bg-emerald-600' },
};

const RESIDENT_TYPE_LABELS = {
  resident_owner: 'Resident Owner',
  non_resident_owner: 'Non-Resident Owner',
  tenant: 'Tenant',
};

export function VillaOccupancyModal({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedVilla, setExpandedVilla] = useState(null);
  const [filter, setFilter] = useState('all');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/community/villa-occupancy`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      if (res.ok) setData(await res.json());
    } catch (err) {
      console.error('Failed to load villa occupancy:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadData();
  }, [open, loadData]);

  const filteredVillas = (data?.villas || []).filter((v) => {
    const matchesSearch =
      !search || v.villa_number.toLowerCase().includes(search.toLowerCase()) ||
      v.occupants.some((o) => o.name.toLowerCase().includes(search.toLowerCase()));
    const matchesFilter = filter === 'all' || v.status === filter;
    return matchesSearch && matchesFilter;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="villa-occupancy-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Home className="w-5 h-5 text-indigo-600" />
            Villa Occupancy Dashboard
          </DialogTitle>
          <DialogDescription>
            Real-time villa allocation and capacity overview
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : data ? (
          <div className="flex-1 overflow-hidden flex flex-col gap-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-3">
              <div className="p-3 bg-indigo-50 rounded-lg text-center" data-testid="villa-total-count">
                <p className="text-2xl font-bold text-indigo-600">{data.total_villas}</p>
                <p className="text-xs text-slate-500">Total Villas</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg text-center" data-testid="villa-full-count">
                <p className="text-2xl font-bold text-red-600">{data.full}</p>
                <p className="text-xs text-slate-500">Full</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg text-center" data-testid="villa-partial-count">
                <p className="text-2xl font-bold text-amber-600">{data.partial}</p>
                <p className="text-xs text-slate-500">Partial</p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg text-center" data-testid="villa-slots-count">
                <p className="text-2xl font-bold text-emerald-600">{data.available_slots}</p>
                <p className="text-xs text-slate-500">Open Slots</p>
              </div>
            </div>

            {/* Search + Filter */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="villa-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search villa or resident..."
                  className="pl-9 h-9"
                />
              </div>
              <div className="flex gap-1">
                {['all', 'full', 'partial'].map((f) => (
                  <Button
                    key={f}
                    size="sm"
                    variant={filter === f ? 'default' : 'outline'}
                    onClick={() => setFilter(f)}
                    className={`text-xs h-9 ${filter === f ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
                    data-testid={`villa-filter-${f}`}
                  >
                    {f === 'all' ? 'All' : f === 'full' ? 'Full' : 'Available'}
                  </Button>
                ))}
              </div>
              <Button size="sm" variant="ghost" onClick={loadData} className="h-9 w-9 p-0" data-testid="villa-refresh-button">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>

            {/* Villa List */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-1" data-testid="villa-list">
              {filteredVillas.length === 0 ? (
                <p className="text-center text-slate-400 py-8 text-sm">
                  {search || filter !== 'all' ? 'No villas match your search' : 'No villas with residents found'}
                </p>
              ) : (
                filteredVillas.map((villa) => {
                  const cfg = STATUS_CONFIG[villa.status];
                  const isExpanded = expandedVilla === villa.villa_number;
                  return (
                    <div
                      key={villa.villa_number}
                      data-testid={`villa-row-${villa.villa_number}`}
                      className={`rounded-lg border transition-all ${cfg.bg}`}
                    >
                      <div
                        className="flex items-center justify-between p-3 cursor-pointer"
                        onClick={() => setExpandedVilla(isExpanded ? null : villa.villa_number)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-white/80 flex items-center justify-center shadow-sm">
                            <Home className={`w-4 h-4 ${cfg.text}`} />
                          </div>
                          <div>
                            <p className="font-semibold text-sm text-slate-800">Villa {villa.villa_number}</p>
                            <p className="text-xs text-slate-500">
                              {villa.count}/{villa.max} occupied
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`${cfg.badge} text-white text-[10px]`}>{cfg.label}</Badge>
                          <div className="flex gap-0.5">
                            {Array.from({ length: villa.max }).map((_, i) => (
                              <div
                                key={`${villa.villa_number}-dot-${i}`}
                                className={`w-2.5 h-2.5 rounded-full ${i < villa.count ? 'bg-indigo-500' : 'bg-slate-300'}`}
                              />
                            ))}
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          )}
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="px-3 pb-3 space-y-2 border-t border-slate-200/50">
                          {villa.occupants.map((occ) => (
                            <div
                              key={occ.email}
                              className="flex items-center justify-between p-2.5 bg-white/70 rounded-lg mt-2"
                            >
                              <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center">
                                  <Users className="w-3.5 h-3.5 text-indigo-600" />
                                </div>
                                <div>
                                  <p className="font-medium text-sm text-slate-800">{occ.name}</p>
                                  <p className="text-xs text-slate-500">{occ.email}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <Badge variant="outline" className="text-[10px]">
                                  {RESIDENT_TYPE_LABELS[occ.resident_type] || 'Resident'}
                                </Badge>
                                {occ.phone && (
                                  <p className="text-[10px] text-slate-400 mt-0.5">{occ.phone}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
